import SystemStat from "@/models/SystemStat";
import { RouterRootState } from "connected-react-router";
import { FC, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import EnrolmentCategoryWizard from "../../../components/enrolmentCategoryWizard/enrolmentCategoryWizard";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { getCountsCategory, TYPE_PER_ENROL_CATEGORY } from "../../../constants/system-constants";
import EnrolmentTab from "../../../containers/enrolment-tab";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import plusIcon from "../../../images/Plus.svg";
import { ButtonTypes } from "../../../models/ButtonTypes";
import { Category } from "../../../models/Category";
import { getCategories } from "../../../services/categoryService";
import { getSystemStats } from "../../../services/systemService";
import { showError } from "../../../services/utilities/ServiceError";
import { isAdminOrAbove, isSystemOwner } from '../../../constants/preset-constants';
import { IAppState } from '../../../store/types';

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

type EnrolmentProps = {
    selected:number
    setSelected:(index:number) => void;
}

const Enrolments:FC<EnrolmentProps> = ({selected,setSelected}) => {
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([]);
    const [tabCategories, setTabCategories] = useState(new Array(0));
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const [showEnrolmentCategoryWizardModal, setShowEnrolmentCategoryWizardModal] = useState(false);
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const { translate } = useLocale();
    const [categoryId, setCategoryId] = useState<number>(-1);
    const [forceRefresh, setForceRefresh] = useState(false);

    const toggleShowNewCategoryWizardModal = () => {
        setShowEnrolmentCategoryWizardModal(!showEnrolmentCategoryWizardModal);
    };

    const resetCategories = async () => {
        try{
            setBusy(true);
            const categoriesData = await getCategories();
            const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
            const systemStats: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_ENROL_CATEGORY) : undefined;
            const categories = categoriesData.map((category) => {
                return {
                    ...category,
                    count: systemStats ? getCountsCategory(systemStats, undefined, category.id.toString()) : 0,
                }
            });

            setCategories([
                {
                    displayName: translate.byKey(TranslationKeys.all),
                    heading: translate.byKey(TranslationKeys.allEnrolments),
                    value: "",
                    id: 0,
                    count: systemStats ? (systemStats.totals.countAll ? parseInt(systemStats.totals.countAll) : 0) : 0,
                },
                ...categories,
            ]);

            if (selected > categories.length) {
                setSelected(0);
            }
            setBusy(false);
        } catch (error) {
            showError(error, toast.warn);
        }
    };

    useEffect(() => {
        resetCategories();
    }, []);

    const setProfileId = () => {
        if (categories.length === 0 || selected === 0 || selected >= categories.length) {
            //Default it to 0 so we don't apply filter and display all enrolments instead
            sessionStorage.setItem('profileId', '0'); 
            return;
        }

        sessionStorage.setItem('profileId', categories[selected].value.toString());
    };

    useEffect(() => {
        if (categories.length > 0) {

            const newTabContent = [
                ...categories.map((c) => (
                    <EnrolmentTab
                        addNewButton={addNewButton}
                        category={c.displayName}
                        tableTitle={c.heading}
                        id={c.id}
                        resetCategories={resetCategories}
                        menuDetails={c.menuDetailsId}
                        key={c.id}
                    ></EnrolmentTab>
                )),
            ];
            setTabContent(newTabContent);
        }
        setTabCategories(getTabsListFromLabels(categories));
        setProfileId();
    }, [categories, selected]);

    const addNewButton = <Link to={{ pathname: "/access-control/enrolment/personnel/" }}>
        <button className={ButtonTypes.BlueButton}>
            {translate.byKey(TranslationKeys.addNew)}
        </button>
    </Link>

    const showCategoryWizard = (categoryIndex) => {
        if (categoryIndex < categories.length) {
            setCategoryId(categories[categoryIndex].id);
            setShowEnrolmentCategoryWizardModal(true);
        }
    }

    const totalEnrolmentsByCategoryId = (newCategoryId) => {
        return categories.find(category => category.id === newCategoryId)?.count;
    }

    return (
        <>
            <ActivityFrame
                busy={busy}
                className="flex flex-grow overflow-y-hidden">
                <div className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
                    <Breadcrumb
                        path={[
                            {
                                value: "/access-control/enrolment",
                                displayName: translate.byKey(TranslationKeys.enrolments),
                            },
                        ]}
                        onClick={(item) => history.push(item.value)} />
                    <Tabs
                        className="overflow-y-hidden"
                        tabs={tabCategories}
                        onSelect={(i) => {
                            setSelected(i);
                        }}
                        selected={selected}
                        onAdd={toggleShowNewCategoryWizardModal}
                        add={isSystemOwner(user.profileId)}
                        tabStyle={{}}
                        onEdit={isSystemOwner(user.profileId) ? showCategoryWizard : undefined}
                        forceRefresh={forceRefresh}
                        setForceRefresh={setForceRefresh}>
                        {tabContent[selected]}
                        <div className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                            <div className="flex flex-row justify-end button-cluster">
                                <Restricted to={`add.${routeName}.element`}>
                                    <Link
                                        to={{ pathname: "/access-control/enrolment/personnel/" }}>
                                        <button className="flex rounded-lg bg-lime text-white p-2 shadow-lg" id='addEnrolmentBtn'>
                                            <img
                                                src={plusIcon}
                                                className="px-2 w-8 py-1"
                                                alt="" />
                                            <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                                        </button>
                                    </Link>
                                </Restricted>
                            </div>
                        </div>
                    </Tabs>
                </div>
                {showEnrolmentCategoryWizardModal
                    ? <EnrolmentCategoryWizard
                        onClose={() => {
                            toggleShowNewCategoryWizardModal();
                            resetCategories();
                            setCategoryId(-1);
                            setForceRefresh(true);
                        }}
                        isOpen={showEnrolmentCategoryWizardModal}
                        setBusy={setBusy}
                        categoryId={categoryId}
                        totalEnrolments={totalEnrolmentsByCategoryId(categoryId)} />
                    : null}
            </ActivityFrame>
        </>
    );
};

export default Enrolments;
